import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import {
  checkSessionOnStartup,
  markSessionActive,
  clearSession,
  startInactivityMonitor,
  stopInactivityMonitor,
  setInactivityTimeoutMinutes,
} from '../lib/sessionSecurity';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(() => {
    const sessionCleared = checkSessionOnStartup();
    if (sessionCleared) return null;
    return localStorage.getItem('token');
  });
  const [loading, setLoading] = useState(true);
  const [showSessionWarning, setShowSessionWarning] = useState(false);

  const performLogout = useCallback(async (revokeOnServer = true) => {
    // Revoke the JWT server-side first so it cannot be reused even if it
    // leaked. Fire-and-forget; we still tear the session down locally if
    // the request fails (e.g. offline).
    if (revokeOnServer) {
      try {
        await api.post('/auth/logout');
      } catch { /* network/server error — proceed with local cleanup */ }
    }
    clearSession();
    localStorage.removeItem('token');
    localStorage.removeItem('mobile_passkey_session');
    localStorage.removeItem('admin_cache');
    setToken(null);
    setAdmin(null);
    setShowSessionWarning(false);
    stopInactivityMonitor();
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        // Rutas públicas (booking, /resultados, /triptico, /firmar, etc.):
        // NO inicializamos la sesión ni arrancamos el monitor de inactividad.
        // De lo contrario, un admin que abre la web pública en otra pestaña
        // (o navega a /resultados desde el propio panel) queda sujeto al
        // auto-logout aunque en realidad no esté "inactivo" en el admin.
        const path = window.location.pathname || '/';
        const isAdminRoute = path.startsWith('/admin');
        if (!isAdminRoute) {
          setLoading(false);
          return;
        }
        // ⚡ Arranque instantáneo: si hay sesión cacheada, pintamos la app
        // YA y validamos /auth/me en segundo plano (si el token fue
        // revocado, el logout llega en <1s y cualquier API devolvería 401
        // igualmente). Ahorra ~0.8-1s de pantalla en blanco por carga.
        let paintedFromCache = false;
        try {
          const cached = localStorage.getItem('admin_cache');
          if (cached) {
            setAdmin(JSON.parse(cached));
            setLoading(false);
            paintedFromCache = true;
            markSessionActive();
            if (localStorage.getItem('mobile_passkey_session') !== '1') {
              startInactivityMonitor(
                (show) => setShowSessionWarning(show),
                () => performLogout(false)
              );
            }
          }
        } catch { /* cache corrupta → flujo normal */ }
        try {
          // Validación + política de inactividad EN PARALELO (antes iban
          // en serie: 2 round-trips ≈ 0.8s en producción).
          const [response, pol] = await Promise.all([
            api.get('/auth/me'),
            api.get('/security/auto-logout-policy').catch(() => null),
          ]);
          setAdmin(response.data);
          try { localStorage.setItem('admin_cache', JSON.stringify(response.data)); } catch { /* quota */ }

          if (pol?.data?.inactivity_minutes) {
            setInactivityTimeoutMinutes(pol.data.inactivity_minutes);
          }

          if (!paintedFromCache) {
            markSessionActive();
            // Sesión móvil por Face ID (superadmin): sin auto-logout por
            // inactividad — la caducidad la impone el JWT de 60 días y la
            // revocación remota desde la consola.
            if (localStorage.getItem('mobile_passkey_session') !== '1') {
              startInactivityMonitor(
                (show) => setShowSessionWarning(show),
                () => performLogout(false)  // auto-logout: token revoked next API call
              );
            }
          }
        } catch (error) {
          const status = error?.response?.status;
          if (status === 401 || status === 403) {
            console.warn('Token expired or invalid, logging out');
            performLogout(false);
          } else {
            console.warn('Auth check failed (network/server), keeping session:', error.message);
          }
        }
      }
      setLoading(false);
    };

    initAuth();
    return () => stopInactivityMonitor();
  }, [token, performLogout]);

  // Silent token refresh every hour to prevent expiration while user is active.
  // We mark this call with `noQueue: true` so the response interceptor's
  // auto-retry / offline-queue logic ignores it. A transient 401 on
  // /auth/refresh would otherwise wipe the admin token even though the
  // current short-lived token is still perfectly valid.
  useEffect(() => {
    if (!token) return;
    // Sesión móvil de 60 días (Face ID superadmin): NO refrescar — el
    // refresh emitiría un token corto normal y acortaría la sesión.
    if (localStorage.getItem('mobile_passkey_session') === '1') return;
    const refreshInterval = setInterval(async () => {
      try {
        const res = await api.post('/auth/refresh', null, { noQueue: true });
        const newToken = res.data?.access_token;
        if (newToken) {
          localStorage.setItem('token', newToken);
          // Don't call setToken to avoid re-triggering initAuth
        }
      } catch {
        // Refresh failed — the current token may still be valid; do
        // nothing. If it really is expired, the next user-driven call
        // will get a 401 and the interceptor will handle redirect.
      }
    }, 60 * 60 * 1000); // Every 1 hour
    return () => clearInterval(refreshInterval);
  }, [token]);

  // Cross-tab session sync: when ANOTHER tab writes/removes the token in
  // localStorage, this tab gets a `storage` event. If the token was cleared
  // (legitimate logout in another tab) AND we are currently inside the
  // admin console, redirect to login. We deliberately do NOT call
  // performLogout here — the other tab already revoked the token server-
  // side; calling /auth/logout again would 401 noisily.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== 'token') return;
      const newValue = e.newValue;
      const inAdminRoute = (window.location.pathname || '/').startsWith('/admin');
      if (!newValue && token) {
        // Another tab logged out.
        clearSession();
        stopInactivityMonitor();
        setToken(null);
        setAdmin(null);
        setShowSessionWarning(false);
        if (inAdminRoute) {
          window.location.href = '/admin/login';
        }
      } else if (newValue && newValue !== token) {
        // Another tab logged in (or rotated the token); pick up the new one
        // so future API calls use it.
        setToken(newValue);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [token]);


  const login = async (email, password, totpCode = null, useRecoveryCode = false) => {
    const response = await api.post('/auth/login', { 
      email, 
      password,
      totp_code: totpCode,
      use_recovery_code: useRecoveryCode
    });
    
    const { access_token, admin: adminData, requires_2fa } = response.data;
    
    if (requires_2fa) {
      return { requires_2fa: true };
    }
    
    localStorage.setItem('token', access_token);
    localStorage.removeItem('mobile_passkey_session');
    try { localStorage.setItem('admin_cache', JSON.stringify(adminData)); } catch { /* quota */ }
    markSessionActive();
    setToken(access_token);
    setAdmin(adminData);

    // Fetch per-role auto-logout policy and apply it before starting the monitor.
    try {
      const pol = await api.get('/security/auto-logout-policy');
      if (pol?.data?.inactivity_minutes) {
        setInactivityTimeoutMinutes(pol.data.inactivity_minutes);
      }
    } catch { /* default 30 min */ }
    
    startInactivityMonitor(
      (show) => setShowSessionWarning(show),
      () => performLogout(false)
    );
    
    return adminData;
  };

  // ==============================
  // "Simular como…" (impersonation)
  // ==============================
  // Superadmin-only. Swap the current session for a fresh token belonging
  // to the target user. The ORIGINAL superadmin token + admin blob are
  // preserved in `sessionStorage` (tab-scoped — never crosses tabs) so
  // we can restore the session without asking for the password again.
  //
  // We deliberately use `sessionStorage` (not localStorage) so closing
  // the tab automatically ends the impersonation and prevents a lingering
  // "restore" affordance in a shared machine.
  const IMP_KEY = 'impersonation_original';

  const [impersonatingBy, setImpersonatingBy] = useState(() => {
    try {
      const raw = sessionStorage.getItem(IMP_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.admin || null;
    } catch { return null; }
  });

  const impersonate = useCallback(async (targetId) => {
    const res = await api.post(`/admin/impersonate/${targetId}`);
    const { access_token, admin: targetAdmin } = res.data || {};
    if (!access_token || !targetAdmin) {
      throw new Error('Respuesta inválida del servidor.');
    }
    // Save the CURRENT session as the "original" — only if we're not
    // already impersonating (avoid nesting).
    if (!sessionStorage.getItem(IMP_KEY)) {
      sessionStorage.setItem(IMP_KEY, JSON.stringify({
        token: localStorage.getItem('token'),
        admin,
      }));
      setImpersonatingBy(admin);
    }
    localStorage.setItem('token', access_token);
    try { localStorage.setItem('admin_cache', JSON.stringify(targetAdmin)); } catch { /* quota */ }
    setToken(access_token);
    setAdmin(targetAdmin);
    return targetAdmin;
  }, [admin]);

  const stopImpersonating = useCallback(async () => {
    let raw;
    try {
      raw = sessionStorage.getItem(IMP_KEY);
    } catch { raw = null; }
    if (!raw) return;
    let parsed;
    try { parsed = JSON.parse(raw); } catch { parsed = null; }
    // Best-effort: revoke the impersonated token server-side too so it
    // can't be reused if the browser cache lingers.
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    sessionStorage.removeItem(IMP_KEY);
    setImpersonatingBy(null);
    if (parsed?.token) {
      localStorage.setItem('token', parsed.token);
      try { localStorage.setItem('admin_cache', JSON.stringify(parsed.admin || null)); } catch { /* quota */ }
      setToken(parsed.token);
      setAdmin(parsed.admin || null);
    } else {
      // Original session lost — force a normal logout so user relogs.
      await performLogout(false);
    }
  }, [performLogout]);

  const logout = useCallback(async () => {
    // If we're impersonating and the user clicks the normal "Cerrar sesión"
    // button, restore the original superadmin session instead of nuking
    // everything — matches the mental model of most SaaS impersonation flows.
    if (sessionStorage.getItem(IMP_KEY)) {
      await stopImpersonating();
      return;
    }
    await performLogout(true);
  }, [performLogout, stopImpersonating]);

  // Sesión móvil de 60 días vía Face ID (solo superadmin): adopta el token
  // emitido por /admin/passkey/login/complete y NO arranca el monitor de
  // inactividad en este dispositivo.
  const adoptPasskeySession = (access_token, adminData) => {
    localStorage.setItem('token', access_token);
    localStorage.setItem('mobile_passkey_session', '1');
    markSessionActive();
    setToken(access_token);
    setAdmin(adminData);
    stopInactivityMonitor();
    return adminData;
  };

  const value = {
    admin,
    user: admin,
    token,
    loading,
    isAuthenticated: !!token && !!admin,
    isAdmin: admin?.role === 'admin' || admin?.role === 'superadmin',
    isSuperAdmin: admin?.role === 'superadmin',
    isDoctor: admin?.role === 'doctor',
    permissions: admin?.permissions || null,
    login,
    adoptPasskeySession,
    logout,
    impersonate,
    stopImpersonating,
    isImpersonating: !!impersonatingBy,
    impersonatingBy,
    showSessionWarning,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
